import type * as THREE from 'three/webgpu';
import { dodgeSourceSignature } from './dodgeClip';
import { ATTACK_CLIP_BASE64, ATTACK_CLIP_METADATA } from './attackClipData';

/** 설치 시 검사할 본 이름/부모/역바인드 지문. 표시 중인 pose와 무관하다. */
export function attackRigSignature(skeleton: THREE.Skeleton): string {
  let a = 0x811c9dc5, b = 5381;
  const scratch = new DataView(new ArrayBuffer(4));
  const byte = (x: number) => { a = Math.imul(a ^ x, 0x01000193) >>> 0; b = (Math.imul(b, 33) ^ x) >>> 0; };
  const uint = (x: number) => { for (let i = 0; i < 4; i++) byte((x >>> (i * 8)) & 255); };
  const string = (x: string) => { uint(x.length); for (let i = 0; i < x.length; i++) uint(x.charCodeAt(i)); };
  uint(skeleton.bones.length);
  for (let i = 0; i < skeleton.bones.length; i++) {
    const bone = skeleton.bones[i]; string(bone.name); string(bone.parent?.name ?? '');
    for (const v of skeleton.boneInverses[i].elements) { scratch.setFloat32(0, v, true); for (let j = 0; j < 4; j++) byte(scratch.getUint8(j)); }
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

/** 원본 분석이 끝난 뒤 표시 전용 복제에 팔6관절만 설치한다. 지문 불일치는 원본 fallback이다. */
export function createAttackRecoveryClip(source: THREE.AnimationClip, skeleton: THREE.Skeleton): THREE.AnimationClip | null {
  if (dodgeSourceSignature(source) !== ATTACK_CLIP_METADATA.sourceSignature || attackRigSignature(skeleton) !== ATTACK_CLIP_METADATA.rigSignature) return null;
  try {
    const binary = atob(ATTACK_CLIP_BASE64);
    if (binary.length !== ATTACK_CLIP_METADATA.float32Bytes) return null;
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < bytes.length; i++) bytes[i] = binary.charCodeAt(i);
    const data = new DataView(bytes.buffer), clone = source.clone();
    clone.name = 'atk2-tail';
    for (const entry of ATTACK_CLIP_METADATA.tracks) {
      const track = clone.tracks.find(t => t.name === entry.name);
      if (!track || track.ValueTypeName !== 'quaternion' || !/^mixamorig(Left|Right)(Arm|ForeArm|Hand)\.quaternion$/.test(track.name)) return null;
      track.times = new Float32Array(entry.timeCount); track.values = new Float32Array(entry.valueCount);
      for (let i = 0; i < track.times.length; i++) track.times[i] = data.getFloat32(entry.timeOffsetBytes + i * 4, true);
      for (let i = 0; i < track.values.length; i++) track.values[i] = data.getFloat32(entry.valueOffsetBytes + i * 4, true);
    }
    return dodgeSourceSignature(clone) === ATTACK_CLIP_METADATA.derivedSignature ? clone : null;
  } catch { return null; }
}

/** 정상 t10 경계에서만 바꾼다. 늦은 진입·남은 mixer fade는 serial 끝까지 원본을 유지한다. */
export class AttackRecoveryGate {
  private serial = -1;
  private observed = false;
  private decided = false;
  private selected = false;
  select(serial: number, tick: number, fullyWeighted: boolean): boolean {
    if (serial !== this.serial) { this.reset(); this.serial = serial; }
    if (tick <= ATTACK_CLIP_METADATA.startTick) { this.observed = true; return false; }
    if (!this.decided) { this.decided = true; this.selected = this.observed && fullyWeighted; }
    return this.selected;
  }
  reset(): void { this.serial = -1; this.observed = this.decided = this.selected = false; }
}
